import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';

function MedicionesChart({ data }) {
  const [activeSeries, setActiveSeries] = useState(null);

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        No hay datos disponibles
      </div>
    );
  }

  // Filtrar datos incompletos; incluir puntos con kW aunque falte frecuencia
  const validData = data.filter(item => {
    if (item.isSyntheticZero) return true;

    const kw = item.kwDesp || item.kwGen || 0;
    const kva = item.kVA || 0;
    const frecuencia = item.frecuencia || 0;
    return kw > 0 || kva > 0 || frecuencia > 40;
  });

  const chartData = validData.map(item => ({
    ...item,
    time: format(new Date(item.timestamp), 'HH:mm:ss'),
    kwDesp: item.kwDesp || item.kwGen || 0,
    kVA: item.kVA || 0,
    cosfi: item.factor_potencia || item.cosfi || 0,
    frecuencia: Number(item.frecuencia) || (item.kwDesp > 0 ? 50 : 0)
  }));

  // Submuestrear datos para mejor rendimiento del gráfico (máximo ~500 puntos)
  // Esto distribuye los puntos uniformemente en todo el rango de tiempo
  const maxPoints = 500;
  let limitedChartData;
  if (chartData.length <= maxPoints) {
    limitedChartData = chartData;
  } else {
    // Calcular el paso para tomar puntos uniformemente distribuidos
    const step = Math.ceil(chartData.length / maxPoints);
    limitedChartData = chartData.filter((_, index) => index % step === 0);
  }

  // Calcular min/max de frecuencia para ajustar la escala
  const frecuencias = limitedChartData.map(d => d.frecuencia).filter(f => f > 0);
  const minFreq = Math.min(...frecuencias, 49.7);
  const maxFreq = Math.max(...frecuencias, 50.3);
  // Ajustar escala para ver variaciones pequeñas
  const freqDomain = [Math.floor(minFreq * 10) / 10, Math.ceil(maxFreq * 10) / 10];

  const handleLegendClick = (e) => {
    const seriesKey = e.dataKey;
    setActiveSeries(prev => prev === seriesKey ? null : seriesKey);
  };

  const getOpacity = (dataKey) => {
    if (!activeSeries) return 1;
    return activeSeries === dataKey ? 1 : 0.1;
  };

  return (
    <div>
      <div className="text-xs text-gray-500 mb-2 text-right">
        Mostrando {limitedChartData.length} de {data.length} registros
      </div>

      {/* Gráfico de Potencias */}
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={limitedChartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="time"
            stroke="#6b7280"
            style={{ fontSize: '10px' }}
            interval="preserveStartEnd"
          />
          <YAxis
            yAxisId="left"
            stroke="#6b7280"
            style={{ fontSize: '10px' }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            stroke="#6b7280"
            style={{ fontSize: '10px' }}
            domain={[0, 1]}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }}
          />
          <Legend
            onClick={handleLegendClick}
            cursor="pointer"
            wrapperStyle={{ paddingTop: '10px' }}
          />
          <Line
            type="monotone"
            dataKey="kwDesp"
            stroke="#0ea5e9"
            strokeWidth={2}
            name="Potencia Activa (kW)"
            dot={false}
            yAxisId="left"
            strokeOpacity={getOpacity('kwDesp')}
            hide={activeSeries && activeSeries !== 'kwDesp'}
          />
          <Line
            type="monotone"
            dataKey="kVA"
            stroke="#8b5cf6"
            strokeWidth={2}
            name="Potencia Aparente (kVA)"
            dot={false}
            yAxisId="left"
            strokeOpacity={getOpacity('kVA')}
            hide={activeSeries && activeSeries !== 'kVA'}
          />
          <Line
            type="monotone"
            dataKey="cosfi"
            stroke="#10b981"
            strokeWidth={2}
            name="Factor de Potencia"
            dot={false}
            yAxisId="right"
            strokeOpacity={getOpacity('cosfi')}
            hide={activeSeries && activeSeries !== 'cosfi'}
          />
        </LineChart>
      </ResponsiveContainer>

      {/* Gráfico de Frecuencia - escala ajustada para ver variaciones */}
      <div className="mt-6">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Frecuencia de Red (Hz)</h4>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={limitedChartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="time"
              stroke="#6b7280"
              style={{ fontSize: '10px' }}
              interval="preserveStartEnd"
            />
            <YAxis
              stroke="#f97316"
              style={{ fontSize: '10px' }}
              domain={freqDomain}
              tickCount={7}
              tickFormatter={(value) => value.toFixed(2)}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }}
              formatter={(value) => [value.toFixed(3) + ' Hz', 'Frecuencia']}
            />
            <Line
              type="monotone"
              dataKey="frecuencia"
              stroke="#f97316"
              strokeWidth={2}
              name="Frecuencia (Hz)"
              dot={false}
            />
            {/* Líneas de referencia */}
            <Line
              type="monotone"
              dataKey={() => 50}
              stroke="#10b981"
              strokeWidth={1}
              strokeDasharray="5 5"
              name="50 Hz (nominal)"
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default MedicionesChart;

